import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, ArrowUpRight, Loader2 } from "lucide-react";

import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, SERVICES } from "../lib/api";
import { cn } from "../lib/utils";

const Booking = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const initialService = useMemo(() => {
    const s = params.get("service");
    return SERVICES.find((x) => x.key === s)?.key || SERVICES[0].key;
  }, [params]);

  const [form, setForm] = useState({
    service_type: initialService,
    full_name: "",
    email: "",
    phone: "",
    address: "",
    preferred_time: "10:00",
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, service_type: initialService }));
  }, [initialService]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Please pick a preferred date");
      return;
    }
    if (!form.full_name || !form.email || !form.phone || !form.address) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
      };
      const { data } = await api.post("/bookings", payload);
      toast.success("Request submitted — we'll be in touch shortly.");
      navigate(`/confirmation/${data.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit booking";
      toast.error(typeof msg === "string" ? msg : "Could not submit booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* LEFT — intro */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <span className="label-eyebrow">— Book a visit</span>
            <h1 className="font-display mt-6 text-5xl lg:text-6xl leading-[0.98]">
              Schedule your
              <br />
              <span className="gold-gradient-text">on-site quote.</span>
            </h1>
            <p className="mt-8 text-muted-foreground leading-relaxed">
              Submit your preferred date and we'll be in touch to confirm. Quotes are
              free and on-site.
            </p>

            <div className="gold-divider my-10 w-32" />

            <div className="space-y-6">
              {SERVICES.map((s) => (
                <button
                  type="button"
                  key={s.key}
                  onClick={() => setForm((f) => ({ ...f, service_type: s.key }))}
                  className={cn(
                    "w-full text-left border hairline p-5 transition-colors",
                    form.service_type === s.key
                      ? "border-primary bg-primary/5"
                      : "hover:border-primary/40 bg-card",
                  )}
                  data-testid={`service-tile-${s.key}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-display text-2xl">{s.short}</p>
                    <span className="text-sm uppercase tracking-[0.22em] text-muted-foreground">{s.duration}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{s.description}</p>
                </button>
              ))}
            </div>
          </aside>

          {/* RIGHT — form */}
          <form
            onSubmit={submit}
            className="lg:col-span-7 border hairline bg-card p-8 lg:p-12 space-y-8"
            data-testid="booking-form"
          >
            <div>
              <Label className="label-eyebrow">Service</Label>
              <Select
                value={form.service_type}
                onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
              >
                <SelectTrigger
                  className="mt-3 h-12 rounded-none bg-secondary border-border focus:ring-1 focus:ring-primary"
                  data-testid="service-select-trigger"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`service-option-${s.key}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-eyebrow">Preferred date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="date-picker-trigger"
                    className={cn(
                      "mt-3 h-12 w-full justify-start rounded-none bg-secondary border-border hover:bg-secondary text-left font-normal",
                      !date && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                    {date ? format(date, "EEE, do MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    data-testid="date-picker-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="full_name" className="label-eyebrow">Full name</Label>
                <Input
                  id="full_name"
                  required
                  value={form.full_name}
                  onChange={onChange("full_name")}
                  className="mt-3 h-12 rounded-none bg-secondary border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="Helena Marsh"
                  data-testid="input-fullname"
                />
              </div>
              <div>
                <Label htmlFor="email" className="label-eyebrow">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  className="mt-3 h-12 rounded-none bg-secondary border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="you@home.com"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="label-eyebrow">Phone</Label>
                <Input
                  id="phone"
                  required
                  value={form.phone}
                  onChange={onChange("phone")}
                  className="mt-3 h-12 rounded-none bg-secondary border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="+1 555 040 2200"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <Label htmlFor="address" className="label-eyebrow">Site address</Label>
                <Input
                  id="address"
                  required
                  value={form.address}
                  onChange={onChange("address")}
                  className="mt-3 h-12 rounded-none bg-secondary border-border focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="City or postcode"
                  data-testid="input-address"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="label-eyebrow">Notes (optional)</Label>
              <Textarea
                id="notes"
                rows={4}
                value={form.notes}
                onChange={onChange("notes")}
                className="mt-3 rounded-none bg-secondary border-border focus:border-primary focus:ring-1 focus:ring-primary resize-none"
                placeholder="Tell us about your space, materials in mind, or any timelines."
                data-testid="input-notes"
              />
            </div>

            <div className="pt-4 flex items-center justify-between gap-4 border-t hairline">
              <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground">
                Reviewed personally · No obligation
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-none bg-primary text-primary-foreground hover:bg-accent px-10 py-6 text-sm uppercase tracking-[0.24em] shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_40px_rgba(212,175,55,0.45)] transition-all"
                data-testid="submit-booking-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting
                  </>
                ) : (
                  <>
                    Request appointment
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Booking;
